import { useTranslation } from 'react-i18next';
import { Sparkline } from '../../components/charts';
import { Button, Card, MetricTile, Money, Skeleton, StateBlock, useDelayed } from '../../components/ui';
import { moneyFormatter } from './format';
import type { Stats } from './stats';
import { useTrend } from './trend';
import type { Remote } from './useRemote';

const SKELETON_TILES = [0, 1, 2, 3];

const Loaded = ({ s }: { s: Stats }) => {
    const { t, i18n } = useTranslation('home');
    const trend = useTrend(s);
    const { symbol, symbolPosition } = s.currency;
    const count = new Intl.NumberFormat(i18n.language).format;
    const money = moneyFormatter(i18n.language, s.currency);
    const period = trend.hourly ? t('tiles.byHour') : t('tiles.byDay');
    const { totals } = s;
    return (
        <>
            <MetricTile
                label={t('tiles.orders')}
                value={count(totals.orders)}
                trend={<Sparkline label={`${t('tiles.orders')}, ${period}`} values={trend.orders} labels={trend.labels} formatValue={count} className="mt-1" />}
                delta={totals.cancelled > 0 ? t('tiles.cancelled', { count: totals.cancelled }) : undefined}
            />
            <MetricTile
                label={t('tiles.revenue')}
                value={<Money amount={totals.revenue} symbol={symbol} position={symbolPosition} className="text-ticket sm:text-display" />}
                trend={<Sparkline label={`${t('tiles.revenue')}, ${period}`} values={trend.revenue} labels={trend.labels} formatValue={money} className="mt-1" />}
                delta={t('tiles.revenueNote')}
            />
            <MetricTile
                label={t('tiles.average')}
                value={
                    totals.averageOrderValue === null ? (
                        <span aria-label={t('tiles.noPaid')}>{'—'}</span>
                    ) : (
                        <Money amount={totals.averageOrderValue} symbol={symbol} position={symbolPosition} className="text-ticket sm:text-display" />
                    )
                }
                delta={totals.averageOrderValue === null ? t('tiles.noPaid') : t('tiles.averageNote')}
            />
            <MetricTile label={t('tiles.items')} value={count(totals.itemsSold)} delta={t('tiles.itemsNote')} />
        </>
    );
};

/** Orders, revenue, average order and items sold, all from GET /stats. Revenue and average count paid, not cancelled orders. */
export const Tiles = ({ remote }: { remote: Remote<Stats> }) => {
    const { t } = useTranslation('home');
    const late = useDelayed();

    if (remote.state === 'loading') {
        return (
            <>
                {SKELETON_TILES.map((i) => (
                    <div key={i} aria-busy="true">
                        <Card>
                            {late && (
                                <>
                                    <Skeleton className="h-3 w-16" />
                                    <Skeleton className="mt-3 h-10 w-24" />
                                </>
                            )}
                        </Card>
                    </div>
                ))}
            </>
        );
    }

    if (remote.state === 'error' || !remote.data) {
        return (
            <Card padding="none" className="col-span-2 lg:col-span-4">
                <StateBlock
                    variant="error"
                    title={t('error.title')}
                    action={
                        <Button variant="strong" onClick={remote.reload}>
                            {t('ui.retry', { ns: 'common' })}
                        </Button>
                    }
                />
            </Card>
        );
    }
    return <Loaded s={remote.data} />;
};
